import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { config } from '../config';

export interface MCPerformanceData {
  mcCompanyId: string;
  mcCompanyName: string;
  totalUsers: number;
  totalContacts: number;
  activeTemplates: number;
  lastActivity?: Date;
  status: string;
  isolationScore: number;
}

export interface RCOversightSummary {
  rcCompanyId: string;
  rcCompanyName: string;
  totalMCs: number;
  totalMCUsers: number;
  totalContacts: number;
  totalAppointments: number;
  avgPerformanceScore: number;
  recentViolations: number;
  managedMCs: MCPerformanceData[];
}

export interface MCUser {
  id: string;
  email: string;
  name: string;
  role: string;
  company: {
    id: string;
    name: string;
  };
  enabled: boolean;
  lastActivity?: Date;
}

export interface CompanyDropdownOption {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class RCOversightService {
  private apiUrl = config.apiUrl;

  constructor(private http: HttpClient) {}

  // Get all MCs managed by this RC
  getManagedMCs(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/api/rc/managed-mcs`);
  }

  // Get performance data for a specific MC
  getMCPerformanceData(mcCompanyId: string): Observable<MCPerformanceData> {
    return this.http.get<MCPerformanceData>(`${this.apiUrl}/api/rc/mc-performance/${mcCompanyId}`);
  }

  // Get comprehensive overview for RC
  getRCOversightSummary(): Observable<RCOversightSummary> {
    return this.http.get<RCOversightSummary>(`${this.apiUrl}/api/rc/oversight-summary`);
  }

  // Create a new MC under RC supervision
  createManagedMC(mcData: { company_name: string; contact_email: string; contact_phone: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/api/rc/create-mc`, mcData);
  }

  // Get all users in RC's supervised MCs
  getAllMCUsers(): Observable<MCUser[]> {
    return this.http.get<MCUser[]>(`${this.apiUrl}/api/rc/mc-users`);
  }

  // Get users for a specific MC
  getMCUsers(mcCompanyId: string): Observable<MCUser[]> {
    return this.http.get<MCUser[]>(`${this.apiUrl}/api/rc/mc-users/${mcCompanyId}`);
  }

  // Get contacts from supervised MCs
  getMCContacts(mcCompanyId?: string): Observable<any> {
    const url = mcCompanyId 
      ? `${this.apiUrl}/api/rc/mc-contacts/${mcCompanyId}`
      : `${this.apiUrl}/api/rc/mc-contacts`;
    return this.http.get<any>(url);
  }

  // Get templates from supervised MCs
  getMCTemplates(mcCompanyId?: string): Observable<any> {
    const url = mcCompanyId 
      ? `${this.apiUrl}/api/rc/mc-templates/${mcCompanyId}`
      : `${this.apiUrl}/api/rc/mc-templates`;
    return this.http.get<any>(url);
  }

  // Get stages from supervised MCs
  getMCStages(mcCompanyId?: string): Observable<any> {
    const url = mcCompanyId 
      ? `${this.apiUrl}/api/rc/mc-stages/${mcCompanyId}`
      : `${this.apiUrl}/api/rc/mc-stages`;
    return this.http.get<any>(url);
  }

  // Get calendar integrations from supervised MCs
  getMCCalendarIntegrations(mcCompanyId?: string): Observable<any[]> {
    const url = mcCompanyId 
      ? `${this.apiUrl}/api/rc/mc-calendars/${mcCompanyId}`
      : `${this.apiUrl}/api/rc/mc-calendars`;
    return this.http.get<any[]>(url);
  }

  // Get RC dashboard metrics
  getRCDashboardMetrics(): Observable<{
    totalMCs: number;
    totalMCUsers: number;
    totalContacts: number;
    totalTemplates: number;
    healthyMCs: number;
    warningMCs: number;
    issuesMCs: number;
    averageIsolationScore: number;
  }> {
    return this.http.get<{
      totalMCs: number;
      totalMCUsers: number;
      totalContacts: number;
      totalTemplates: number;
      healthyMCs: number;
      warningMCs: number;
      issuesMCs: number;
      averageIsolationScore: number;
    }>(`${this.apiUrl}/api/rc/dashboard-metrics`);
  }

  // Validate RC can perform action on MC
  validateRCAccess(mcCompanyId: string): Observable<{ canAccess: boolean; reason?: string }> {
    return this.http.get<{ canAccess: boolean; reason?: string }>(
      `${this.apiUrl}/api/rc/validate-access/${mcCompanyId}`
    );
  }

  // Get MC companies for dropdown (for forms)
  getMCCompaniesDropdown(): Observable<CompanyDropdownOption[]> {
    return this.http.get<CompanyDropdownOption[]>(`${this.apiUrl}/api/rc/mc-companies-dropdown`);
  }

  // Get channels from supervised MCs
  getMCChannels(mcCompanyId?: string): Observable<any> {
    const url = mcCompanyId 
      ? `${this.apiUrl}/api/rc/mc-channels/${mcCompanyId}`
      : `${this.apiUrl}/api/rc/mc-channels`;
    return this.http.get<any>(url);
  }

  // Get interviews from supervised MCs
  getMCInterviews(params?: any): Observable<any> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<any>(`${this.apiUrl}/api/rc/mc-interviews${queryString}`);
  }

  // Get interview statistics from supervised MCs
  getMCInterviewStats(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/rc/mc-interviews/stats`);
  }

  // Get conversations from supervised MCs (read-only for RC)
  getMCConversations(mcCompanyId?: string): Observable<any> {
    const url = mcCompanyId 
      ? `${this.apiUrl}/api/rc/mc-conversations/${mcCompanyId}`
      : `${this.apiUrl}/api/rc/mc-conversations`;
    return this.http.get<any>(url);
  }

  // Get messages from MC conversations (read-only for RC)
  getMCConversationMessages(conversationId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/rc/mc-conversation-messages/${conversationId}`);
  }
}